import { css, cx } from "@emotion/css";
import { CaretDownIcon, ArrowUpRightFromSquareIcon, ArrowDownToSquareIcon } from "@octopusdeploy/design-system-icons";
import React, { forwardRef } from "react";
import type { DesignSystemLinkHref } from "../../routing";
import { useOctopusLinkComponent } from "../../routing";
import type { SupportedAriaPopupRole } from "../../utils/SupportedAriaPopupRole";
import { getButtonStyles, buttonLabelStyles, getButtonIconStyles } from "./buttonStyles";
interface SharedButtonProps {
    /**
     * The importance of the button.
     */
    importance: ButtonImportance;
    /**
     * Controls whether the button is disabled.
     */
    disabled?: boolean;
    /**
     * The size of the button.
     */
    size?: ButtonSize;
    /**
     * The title to show when hovering the button.
     */
    title?: string;
    /**
     * The accessible name for the button.
     */
    accessibleName?: string;
    /**
     * The button icon. Will display to the left of the label if it exists.
     */
    icon?: React.ReactNode;
}
export interface ButtonLinkProps extends SharedButtonProps {
    /**
     * The link href.
     */
    href: DesignSystemLinkHref;
    /**
     * Set this to true for links that navigate outside of the application.
     */
    external?: boolean;
    /**
     * Controls whether the link should open in a new tab.
     * For external links, this is always true and cannot be configured.
     */
    opensInNewTab?: boolean;
    /**
     * Adds the download icon to the button and if the button is also external it will hide the external link icon
     * (Both icons are not necessary).
     */
    isDownload?: boolean;
    /**
     * The link label.
     */
    label: string;
    /**
     * Optional callback for when the link is clicked.
     */
    onClick?: ClickHandler;
}
export interface ButtonBasicProps extends SharedButtonProps {
    /**
     * Callback for when the button is clicked.
     */
    onClick: ClickHandler;
    /**
     * The button label.
     */
    label?: string;
    /**
     * Display a custom menu caret icon next to the label.
     */
    showExpandCollapseIcon?: boolean;
    /**
     * Identifies the element (or elements) whose contents or presence are controlled by the button.
     */
    "aria-controls"?: string;
    /**
     * Indicates the availability and type of interactive popup element that can be triggered by an element.
     */
    "aria-haspopup"?: SupportedAriaPopupRole;
    /**
     * Indicates whether the  element this button controls is currently expanded or collapsed.
     */
    "aria-expanded"?: "true" | "false";
}
type ClickHandler = (event: React.MouseEvent) => void;
export type ButtonProps = ButtonBasicProps | ButtonLinkProps;
export type IconOnlyButtonProps = Omit<ButtonProps, "label"> & {
    icon: React.ReactNode;
    label?: never;
};
export const Button = forwardRef<HTMLElement, ButtonProps>((props: ButtonProps, ref) => {
    const title = props.title ?? props.label;
    if (isLinkButtonProps(props)) {
        return (<LinkButton 
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ref={ref as React.ForwardedRef<HTMLAnchorElement>} label={props.label} href={props.href} onClick={props.onClick} importance={props.importance} size={props.size} disabled={props.disabled} icon={props.icon} external={props.external} isDownload={props.isDownload} opensInNewTab={props.opensInNewTab} title={title} accessibleName={props.accessibleName}/>);
    }
    return (<BasicButton 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ref={ref as React.ForwardedRef<HTMLButtonElement>} onClick={props.onClick} label={props.label} importance={props.importance} size={props.size} disabled={props.disabled} icon={props.icon} showExpandCollapseIcon={props.showExpandCollapseIcon} title={title} accessibleName={props.accessibleName} aria-controls={props["aria-controls"]} aria-haspopup={props["aria-haspopup"]} aria-expanded={props["aria-expanded"]}/>);
});
Button.displayName = "Button";
function isLinkButtonProps(props: ButtonProps): props is ButtonLinkProps {
    return "href" in props && !!props.href;
}
const LinkButton = forwardRef<HTMLAnchorElement, ButtonLinkProps>(({ external, isDownload, disabled = false, size = "medium", onClick, href, importance, label, title, accessibleName, icon, ...props }, ref) => {
    const Link = useOctopusLinkComponent();
    const buttonStyles = getButtonStyles(importance, disabled, size, !!label);
    const buttonIconStyles = getButtonIconStyles(size);
    const opensInNewTab = external ? true : props.opensInNewTab;
    const showExternalLinkIcon = opensInNewTab && !isDownload;
    const renderIcon = isDownload ? (<span className={buttonIconStyles}>
            <ArrowDownToSquareIcon size={20}/>
        </span>) : (icon && <span className={buttonIconStyles}>{icon}</span>);
    const handleOnClick: React.MouseEventHandler = React.useCallback((event) => {
        onClick?.(event);
    }, [onClick]);
    const linkContent = (<>
            {renderIcon}
            {label && <span className={buttonLabelStyles}>{label}</span>}
            {showExternalLinkIcon && (<span className={buttonIconStyles}>
                    <ArrowUpRightFromSquareIcon size={20}/>
                </span>)}
        </>);
    if (disabled) {
        return (<a ref={ref} role="link" className={buttonStyles} aria-disabled={true} title={title}>
                {linkContent}
            </a>);
    }
    const target = opensInNewTab ? "_blank" : "_self";
    if (external) {
        const stringHref = typeof href === "string" ? href : undefined;
        return (<a ref={ref} className={buttonStyles} target={target} rel="noreferrer" href={stringHref} onClick={handleOnClick} title={title}>
                {linkContent}
            </a>);
    }
    return (<Link ref={ref} className={buttonStyles} target={target} href={href} onClick={handleOnClick} aria-label={accessibleName} title={title}>
            {linkContent}
        </Link>);
});
LinkButton.displayName = "LinkButton";
const BasicButton = forwardRef<HTMLButtonElement, ButtonBasicProps>(({ icon, importance, size = "medium", onClick, disabled = false, label, title, accessibleName, "aria-controls": ariaControls, "aria-haspopup": ariaHasPopup, "aria-expanded": ariaExpanded, showExpandCollapseIcon }, ref) => {
    const buttonStyles = getButtonStyles(importance, disabled, size, !!label);
    const buttonIconStyles = getButtonIconStyles(size);
    // FFT TODO Temporary - need to update every instance to set menuIcon explicitly
    // eslint-disable-next-line prettier/prettier
    const showMenuIcon = ariaExpanded ? showExpandCollapseIcon ?? true : false;
    const caretIcon = <CaretDownIcon size={20}/>;
    return (<button ref={ref} className={buttonStyles} onClick={onClick} disabled={disabled} title={title ?? label} aria-label={accessibleName} aria-controls={ariaControls} aria-haspopup={ariaHasPopup} aria-expanded={ariaExpanded}>
                {icon && <span className={buttonIconStyles}>{icon}</span>}
                {label && <span className={buttonLabelStyles}>{label}</span>}
                {label && showMenuIcon && <span className={cx(buttonIconStyles, buttonMenuStyles, { [buttonMenuExpandedStyles]: ariaExpanded === "true" })}>{caretIcon}</span>}
            </button>);
});
BasicButton.displayName = "BasicButton";
export const buttonImportanceLevels = ["primary", "secondary", "tertiary", "destructive", "quiet", "loud", "neutral", "ghost"] as const;
export type ButtonImportance = (typeof buttonImportanceLevels)[number];
export const buttonSizes = ["medium", "small"] as const;
export type ButtonSize = (typeof buttonSizes)[number];
const buttonMenuStyles = css({
    transition: "transform 150ms ease-in-out",
});
const buttonMenuExpandedStyles = css({
    transform: "rotate(-180deg)",
});
